import { useMemo, useState } from "react";
import { Calculator, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const META = {
  "EUR/USD": { multiplier: 100000, pip: 0.0001, decimals: 5, kind: "usd_quote" },
  "GBP/USD": { multiplier: 100000, pip: 0.0001, decimals: 5, kind: "usd_quote" },
  "AUD/USD": { multiplier: 100000, pip: 0.0001, decimals: 5, kind: "usd_quote" },
  "NZD/USD": { multiplier: 100000, pip: 0.0001, decimals: 5, kind: "usd_quote" },
  "USD/JPY": { multiplier: 100000, pip: 0.01,   decimals: 3, kind: "usd_base" },
  "USD/CAD": { multiplier: 100000, pip: 0.0001, decimals: 5, kind: "usd_base" },
  "USD/CHF": { multiplier: 100000, pip: 0.0001, decimals: 5, kind: "usd_base" },
  "XAU/USD": { multiplier: 100,    pip: 0.01,   decimals: 2, kind: "metal"    },
};

/** Loss-per-1-lot in USD for a given absolute price distance. */
function lossPerLot(instrument, distance, refPrice) {
  const m = META[instrument];
  if (!m) return 0;
  if (m.kind === "usd_base" && refPrice > 0) {
    return (distance * m.multiplier) / refPrice;
  }
  return distance * m.multiplier;
}

export default function RiskCalculator({
  instrument,
  entryPrice,
  stopLoss,
  clientBalance,
  onApplyLots,
}) {
  const [riskMode, setRiskMode] = useState("usd"); // "usd" | "pct"
  const [riskUsd, setRiskUsd] = useState("100");
  const [riskPct, setRiskPct] = useState("2");

  const entry = parseFloat(entryPrice);
  const sl = parseFloat(stopLoss);
  const m = META[instrument];

  const riskAmount = useMemo(() => {
    if (riskMode === "usd") return parseFloat(riskUsd) || 0;
    const pct = parseFloat(riskPct) || 0;
    return ((clientBalance || 0) * pct) / 100;
  }, [riskMode, riskUsd, riskPct, clientBalance]);

  const distance = isFinite(entry) && isFinite(sl) ? Math.abs(entry - sl) : 0;
  const pips = m && distance > 0 ? distance / m.pip : 0;
  const pipsLabel = m && (m.kind === "metal" ? "points" : "pips");

  const lossPerOneLot = m ? lossPerLot(instrument, distance, entry || sl) : 0;
  const suggestedLots = lossPerOneLot > 0 && riskAmount > 0 ? riskAmount / lossPerOneLot : 0;

  const valid = riskAmount > 0 && distance > 0 && suggestedLots > 0;

  return (
    <div className="border border-subtle bg-background p-5 space-y-4" data-testid="risk-calculator">
      <div className="flex items-center gap-2">
        <Calculator className="w-4 h-4 text-primary" />
        <h4 className="text-sm font-semibold tracking-tight">Position Size Calculator</h4>
      </div>

      <div className="grid grid-cols-2 gap-px bg-border">
        <button
          type="button"
          onClick={() => setRiskMode("usd")}
          data-testid="risk-mode-usd"
          className={`px-3 py-2 text-xs font-semibold transition-colors ${
            riskMode === "usd" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-surface-hover"
          }`}
        >
          USD AMOUNT
        </button>
        <button
          type="button"
          onClick={() => setRiskMode("pct")}
          data-testid="risk-mode-pct"
          className={`px-3 py-2 text-xs font-semibold transition-colors ${
            riskMode === "pct" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-surface-hover"
          }`}
        >
          % OF BALANCE
        </button>
      </div>

      {riskMode === "usd" ? (
        <div>
          <Label className="label-eyebrow">Risk Amount (USD)</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={riskUsd}
            onChange={(e) => setRiskUsd(e.target.value)}
            className="mt-2 bg-card border-subtle font-mono-num h-10"
            data-testid="risk-usd-input"
          />
        </div>
      ) : (
        <div>
          <Label className="label-eyebrow">
            Risk % of Balance{clientBalance > 0 && (
              <span className="text-muted-foreground normal-case tracking-normal font-normal"> · Balance ${clientBalance.toFixed(2)}</span>
            )}
          </Label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={riskPct}
            onChange={(e) => setRiskPct(e.target.value)}
            className="mt-2 bg-card border-subtle font-mono-num h-10"
            data-testid="risk-pct-input"
          />
          <p className="text-xs text-muted-foreground mt-1.5 font-mono-num">
            = ${riskAmount.toFixed(2)} USD
          </p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-px bg-border text-center">
        <div className="bg-card p-3">
          <div className="label-eyebrow mb-1">Distance</div>
          <div className="font-mono-num text-sm font-semibold">
            {pips > 0 ? `${pips.toFixed(1)} ${pipsLabel}` : "—"}
          </div>
        </div>
        <div className="bg-card p-3">
          <div className="label-eyebrow mb-1">Risk per Lot</div>
          <div className="font-mono-num text-sm font-semibold">
            {lossPerOneLot > 0 ? `$${lossPerOneLot.toFixed(2)}` : "—"}
          </div>
        </div>
        <div className="bg-card p-3">
          <div className="label-eyebrow mb-1">Suggested Lots</div>
          <div className={`font-mono-num text-sm font-semibold ${valid ? "text-primary" : "text-muted-foreground"}`} data-testid="suggested-lots">
            {valid ? suggestedLots.toFixed(2) : "—"}
          </div>
        </div>
      </div>

      <button
        type="button"
        disabled={!valid}
        onClick={() => onApplyLots(parseFloat(suggestedLots.toFixed(2)))}
        data-testid="apply-suggested-lots"
        className="w-full h-9 rounded text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center gap-2"
      >
        Apply {valid ? suggestedLots.toFixed(2) : "—"} lots to position
        <ArrowRight className="w-3.5 h-3.5" />
      </button>

      {!valid && (
        <p className="text-xs text-muted-foreground">
          Enter both <span className="text-foreground">Entry Price</span> and <span className="text-foreground">Stop Loss</span> above to compute size.
        </p>
      )}
    </div>
  );
}
